import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import TreatmentSection from "../../components/patientChart/TreatmentSection";
import { buildTreatmentNarrative } from "../../components/patientChart/treatmentUtils";
import RXSection from "../../components/patientChart/RXSection";
import IncidentSection from "../../components/patientChart/IncidentSection";
import DocumentsSection from "../../components/patientChart/sections/DocumentsSection";
import SignatureSection from "../../components/patientChart/SignatureSection";
import {
  generateIncidentReportPdf,
  generateRxPrescriptionPdf,
} from "../../services/documentGenerationService";
import { buildIncidentReportTemplateData } from "../../utils/patient/incidentReportMapper";
import { db } from "../../lib/firebase";
import ScanDocumentModal from "../../components/patientChart/ScanDocumentModal";
import PatientInfoSection from "../../components/patientChart/PatientInfoSection";
import EncounterSummarySection from "../../components/patientChart/EncounterSummarySection";
import VitalsSection from "../../components/patientChart/sections/VitalsSection";
import MedicalSection from "../../components/patientChart/sections/MedicalSection";
import {
  blankIncident,
  claimPatientChartLock,
  createEncounter,
  getPatientById,
  listEncounters,
  releasePatientChartLock,
  touchPatientChartLock,
  updateEncounter,
  uploadEncounterDocument,
  verifyPatientChartLock,
} from "../../services/patientService";
import {
  formatDateForDisplay,
  formatDateTimeForDisplay,
} from "../../utils/patient/patientUtils";

const pageStyle = {
  background: "linear-gradient(180deg, #eef4ff 0%, #f8fafc 100%)",
  minHeight: "100vh",
  padding: "18px",
  width: "100%",
  maxWidth: "100%",
  overflowX: "hidden",
  boxSizing: "border-box",
};

const basePanelStyle = {
  border: "1px solid #dbeafe",
  borderRadius: 18,
  background: "#ffffff",
  padding: 16,
  scrollMarginTop: 135,
  boxShadow: "0 12px 28px rgba(15,23,42,.08)",
};

const sectionTitleStyle = {
  fontSize: 17,
  fontWeight: 800,
  marginBottom: 0,
};

const buttonStyle = {
  border: "none",
  borderRadius: 12,
  minHeight: 46,
  padding: "0 18px",
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 800,
  boxShadow: "0 4px 10px rgba(0,0,0,.08)",
  transition: "all .2s ease",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff",
};

const createButtonStyle = {
  ...buttonStyle,
  background: "linear-gradient(135deg,#16a34a,#15803d)",
  color: "#fff",
};

const backButtonStyle = {
  ...buttonStyle,
  background: "#7c3aed",
  color: "#fff",
  borderColor: "#7c3aed",
};

const dangerButtonStyle = {
  ...buttonStyle,
  color: "#b91c1c",
  borderColor: "#fecaca",
  background: "#fff",
};

const jumpButtonStyle = {
  ...buttonStyle,
  minHeight: 40,
  padding: "0 12px",
  fontSize: 13,
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 13,
  height: 38,
  background: "#fff",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 56,
  height: "auto",
  resize: "none",
  overflow: "hidden",
};

const labelStyle = {
  display: "grid",
  gap: 3,
  fontSize: 12,
  color: "#475569",
};

const sectionHeaderRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 10,
};

const toggleButtonStyle = {
  ...buttonStyle,
  padding: "6px 10px",
  fontSize: 12,
};

const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 700,
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 20,
};

const overlayCardStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
  minWidth: 280,
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  display: "grid",
  gap: 14,
  justifyItems: "center",
  textAlign: "center",
};

const spinnerStyle = {
  width: 42,
  height: 42,
  border: "4px solid #dbeafe",
  borderTop: "4px solid #0f766e",
  borderRadius: "50%",
  animation: "incident-spin 1s linear infinite",
};

const rhythmOptions = [
  "NSR",
  "Sinus Tach",
  "Sinus Brady",
  "A-Fib",
  "A-Flutter",
  "SVT",
  "VT",
  "VF",
  "PEA",
  "Asystole",
  "Other",
];

const painOptions = ["", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const gcsEyeOptions = [
  { value: "", label: "Select" },
  { value: "4", label: "4 - Spontaneous" },
  { value: "3", label: "3 - To voice" },
  { value: "2", label: "2 - To pain" },
  { value: "1", label: "1 - None" },
];

const gcsVerbalOptions = [
  { value: "", label: "Select" },
  { value: "5", label: "5 - Oriented" },
  { value: "4", label: "4 - Confused" },
  { value: "3", label: "3 - Inappropriate words" },
  { value: "2", label: "2 - Incomprehensible sounds" },
  { value: "1", label: "1 - None" },
];

const gcsMotorOptions = [
  { value: "", label: "Select" },
  { value: "6", label: "6 - Obeys commands" },
  { value: "5", label: "5 - Localizes pain" },
  { value: "4", label: "4 - Withdraws from pain" },
  { value: "3", label: "3 - Flexion to pain" },
  { value: "2", label: "2 - Extension to pain" },
  { value: "1", label: "1 - None" },
];

const sectionThemes = {
  patientInfo: {
    border: "#93c5fd",
    accent: "#2563eb",
    bg: "linear-gradient(135deg, #dbeafe 0%, #eff6ff 45%, #ffffff 100%)",
    text: "#1e40af",
  },
  summary: {
    border: "#94a3b8",
    accent: "#0f172a",
    bg: "linear-gradient(135deg, #e2e8f0 0%, #f8fafc 55%, #ffffff 100%)",
    text: "#0f172a",
  },
  vitals: {
    border: "#5eead4",
    accent: "#0f766e",
    bg: "linear-gradient(135deg, #ccfbf1 0%, #ecfeff 50%, #ffffff 100%)",
    text: "#0f766e",
  },
  documents: {
    border: "#fbbf24",
    accent: "#d97706",
    bg: "linear-gradient(135deg, #fef3c7 0%, #fffbeb 50%, #ffffff 100%)",
    text: "#92400e",
  },
  medical: {
    border: "#86efac",
    accent: "#16a34a",
    bg: "linear-gradient(135deg, #dcfce7 0%, #f0fdf4 50%, #ffffff 100%)",
    text: "#166534",
  },
  procedures: {
  accent: "#2563eb",
  badgeBg: "#dbeafe",
  badgeColor: "#1d4ed8",
},

interventions: {
  accent: "#059669",
  badgeBg: "#d1fae5",
  badgeColor: "#047857",
},

assessments: {
  accent: "#7c3aed",
  badgeBg: "#ede9fe",
  badgeColor: "#6d28d9",
},

medications: {
  accent: "#dc2626",
  badgeBg: "#fee2e2",
  badgeColor: "#b91c1c",
},
  treatments: {
    border: "#c4b5fd",
    accent: "#7c3aed",
    bg: "linear-gradient(135deg, #ede9fe 0%, #f5f3ff 50%, #ffffff 100%)",
    text: "#5b21b6",
  },
  incident: {
    border: "#fb7185",
    accent: "#e11d48",
    bg: "linear-gradient(135deg, #ffe4e6 0%, #fff1f2 50%, #ffffff 100%)",
    text: "#be123c",
  },
  signatures: {
    border: "#a5b4fc",
    accent: "#4f46e5",
    bg: "linear-gradient(135deg, #e0e7ff 0%, #eef2ff 50%, #ffffff 100%)",
    text: "#3730a3",
  },
  history: {
    border: "#cbd5e1",
    accent: "#334155",
    bg: "linear-gradient(135deg, #f1f5f9 0%, #f8fafc 50%, #ffffff 100%)",
    text: "#1e293b",
  },
};

function AutoTextarea({ value, onChange, style, ...props }) {
  const textareaRef = useRef(null);

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    resize();
  }, [value]);

  return (
    <textarea
      {...props}
      ref={textareaRef}
      value={value}
      onChange={(e) => {
        onChange(e);
        requestAnimationFrame(resize);
      }}
      style={{
        ...textareaStyle,
        ...style,
      }}
    />
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>{label}</div>
      <div style={{ fontWeight: 700 }}>{value || "—"}</div>
    </div>
  );
}

function SectionCard({
  title,
  sectionKey,
  expandedSections,
  toggleSection,
  children,
  sectionRef,
  extraHeaderContent = null,
  activeSection,
}) {
  const isExpanded = expandedSections[sectionKey];
  const theme = sectionThemes[sectionKey] || sectionThemes.summary;
  const isActive = activeSection === sectionKey;

  return (
    <div
      ref={sectionRef}
      data-section-key={sectionKey}
      style={{
        ...basePanelStyle,
        border: `1px solid ${theme.border}`,
        borderTop: `5px solid ${theme.accent}`,
        boxShadow: isActive
          ? `0 0 0 2px ${theme.border}, 0 12px 24px rgba(15, 23, 42, 0.07)`
          : basePanelStyle.boxShadow,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <div
        style={{
          ...sectionHeaderRowStyle,
          margin: "-14px -14px 12px -14px",
          padding: "10px 14px",
          background: theme.bg,
          borderTopLeftRadius: 11,
          borderTopRightRadius: 11,
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <div
          style={{
            ...sectionTitleStyle,
            color: theme.text,
          }}
        >
          {title}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {extraHeaderContent}
          <button
            type="button"
            style={{
              ...toggleButtonStyle,
              borderColor: theme.border,
              color: theme.text,
              background: "#fff",
            }}
            onClick={() => toggleSection(sectionKey)}
          >
            {isExpanded ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      {isExpanded ? children : <div style={{ color: "#64748b", fontSize: 13 }}>Section collapsed.</div>}
    </div>
  );
}

const blankMedicalForm = {
  hpiComplaint: "",
  assessment: "",
  planTreatmentDisposition: "",
  treatments: [],
  bodyPartInjured: "",
  firstAidGiven: false,
  disposition: "",
};

const blankEncounterForm = {
  status: "open",
  arrivalDate: "",
  arrivalTime: "",
  chiefComplaint: "",
  bed: "",
  seenAt: "HQ",
  source: "",
  providerName: "",
  reportCompletedBy: "",
  notes: "",
  incidentRequired: false,
  incidentStatus: "not-required",
};

const blankVital = () => ({
  time: new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  }),
  systolic: "",
  diastolic: "",
  bp: "",
  pulse: "",
  resp: "",
  spo2: "",
  tempValue: "",
  tempUnit: "F",
  temp: "",
  pain: "",
  gcsEye: "",
  gcsVerbal: "",
  gcsMotor: "",
  gcs: "",
  etco2: "",
  rhythm: "",
  rhythmOther: "",
  notes: "",
});

const defaultExpandedSections = {
  patientInfo: true,
  summary: true,
  vitals: true,
  documents: true,
  medical: true,
  procedures: true,
  interventions: true,
  assessments: true,
  medications: true,
  rx: true,
  incident: true,
  signatures: true,
  history: true,
};

const sectionOrder = [
  "patientInfo",
  "summary",
  "vitals",
  "documents",
  "medical",
  "procedures",
  "interventions",
  "assessments",
  "medications",
  "rx",
  "incident",
  "signatures",
  "history",
];

function digitsOnly(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatPhone(value = "") {
  const digits = digitsOnly(value).slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function parseBp(bp = "") {
  const [systolic = "", diastolic = ""] = String(bp).split("/");
  return {
    systolic: digitsOnly(systolic).slice(0, 3),
    diastolic: digitsOnly(diastolic).slice(0, 3),
  };
}

function parseTemp(temp = "") {
  const raw = String(temp || "").trim();
  if (!raw) return { tempValue: "", tempUnit: "F" };
  const unit = /c$/i.test(raw) ? "C" : "F";
  const tempValue = raw.replace(/[^\d.]/g, "");
  return { tempValue, tempUnit: unit };
}

function getGcsTotal(eye, verbal, motor) {
  const total = Number(eye || 0) + Number(verbal || 0) + Number(motor || 0);
  if (!eye && !verbal && !motor) return "";
  return String(total);
}

function buildBpString(systolic, diastolic) {
  if (!systolic && !diastolic) return "";
  if (systolic && diastolic) return `${systolic}/${diastolic}`;
  return systolic || diastolic || "";
}

function buildTempString(tempValue, tempUnit) {
  if (!tempValue) return "";
  return `${tempValue}°${tempUnit || "F"}`;
}

function getPatientAddressDisplay(patient) {
  const street = patient?.streetAddress || "";
  const city = patient?.city || "";
  const state = patient?.state || "";
  const zip = patient?.zip || "";

  const cityState = [city, state].filter(Boolean).join(", ");
  const line2 = [cityState, zip].filter(Boolean).join(" ");
  const combined = [street, line2].filter(Boolean).join(", ");
  return combined || patient?.address || "—";
}

function getDisplayValueWithNone(textValue, noneFlag) {
  if (noneFlag) return "None";
  return textValue || "—";
}

function getEmployeeDisplayName(employee = {}) {
  const name =
    employee.displayName ||
    employee.fullName ||
    [employee.firstName, employee.lastName].filter(Boolean).join(" ") ||
    employee.name ||
    employee.email ||
    "";

  const level =
    employee.level ||
    employee.position ||
    employee.roleLabel ||
    employee.providerLevel ||
    employee.credential ||
    employee.title ||
    "";

  return level ? `${name}, ${level}` : name;
}

function isProviderEmployee(employee = {}) {
  const haystack = [
    employee.level,
    employee.position,
    employee.roleLabel,
    employee.providerLevel,
    employee.credential,
    employee.title,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    haystack.includes("physician") ||
    haystack.includes("aprn") ||
    haystack.includes("pa") ||
    haystack.includes("provider")
  );
}

function isRxPrescriberEmployee(employee = {}) {
  const haystack = [
    employee.position,
    employee.basePosition,
    employee.providerLevel,
    employee.roleLabel,
    employee.title,
    employee.level,
    employee.applicationData?.position,
    employee.applicationData?.providerLevel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    haystack.includes("physician") ||
    haystack.includes("resident") ||
    haystack.includes("aprn") ||
    haystack.includes("physician assistant") ||
    /\bpa-c\b/.test(haystack) ||
    /\bpa\b/.test(haystack)
  );
}

function compactHistoryStatus(status = "") {
  if (!status) return "—";
  return String(status)
    .replace(/([A-Z])/g, " $1")
    .replace(/\s+/g, " ")
    .trim();
}

function getStatusPillStyle(status = "") {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "open" || normalized === "under care") {
    return {
      ...pillStyle,
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (normalized === "awaiting transport") {
    return {
      ...pillStyle,
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  if (normalized === "sent to hospital" || normalized === "senttohospital") {
    return {
      ...pillStyle,
      background: "#fee2e2",
      color: "#b91c1c",
    };
  }

  if (normalized === "closed" || normalized === "discharged") {
    return {
      ...pillStyle,
      background: "#e2e8f0",
      color: "#475569",
    };
  }

  return {
    ...pillStyle,
    background: "#f1f5f9",
    color: "#475569",
  };
}

function buildIncidentDocumentRecord({ patient, encounterForm, result }) {
  const displayName = patient?.fullName || "Patient";
  const datePart =
    encounterForm?.arrivalDate || new Date().toISOString().slice(0, 10);
  const fileName =
    result?.fileName || `Incident Report - ${displayName} - ${datePart}.pdf`;

  return {
    id: `incident-${Date.now()}`,
    name: fileName,
    fileName,
    url: result?.pdfUrl || "",
    downloadURL: result?.pdfUrl || "",
    uploadedAt: result?.generatedAt || new Date().toISOString(),
    createdAt: result?.generatedAt || new Date().toISOString(),
    type: "application/pdf",
    mimeType: "application/pdf",
    category: "incident-report",
    source: "cloud-function",
    driveFileId: result?.driveFileId || "",
    templateId: result?.templateId || "",
    generatedAt: result?.generatedAt || new Date().toISOString(),
  };
}

function buildRxDocumentRecord({ patient, encounterForm, result }) {
  const displayName = patient?.fullName || "Patient";
  const datePart =
    encounterForm?.arrivalDate || new Date().toISOString().slice(0, 10);

  const fileName =
    result?.fileName || `RX - ${displayName} - ${datePart}.pdf`;

  return {
    id: `rx-${Date.now()}`,
    name: fileName,
    fileName,
    url: result?.pdfUrl || "",
    downloadURL: result?.pdfUrl || "",
    uploadedAt: result?.generatedAt || new Date().toISOString(),
    createdAt: result?.generatedAt || new Date().toISOString(),
    type: "application/pdf",
    mimeType: "application/pdf",
    category: "rx-prescription",
    source: "cloud-function",
    driveFileId: result?.driveFileId || "",
    generatedAt: result?.generatedAt || new Date().toISOString(),
  };
}

export default function PatientChartPage() {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isHQMode = location.pathname.startsWith("/field-access/hq/");

  const [patient, setPatient] = useState(null);
  
  function setPatientField(field, value) {
  if (readOnlyRef.current) return;
  setPatient((prev) => ({
    ...prev,
    [field]: value,
  }));
}
function setSignatureField(field, value) {
  if (readOnlyRef.current) return;
  setSignatureForm((prev) => ({
    ...prev,
    [field]: value,
  }));
}
  const [encounters, setEncounters] = useState([]);
  const [selectedEncounterId, setSelectedEncounterId] = useState("");
  const [encounterForm, setEncounterForm] = useState(blankEncounterForm);
  const [medicalForm, setMedicalForm] = useState(blankMedicalForm);
  const [incidentForm, setIncidentForm] = useState(blankIncident());
  const [signatureForm, setSignatureForm] = useState({
    patientSignature: "",
    providerSignature: "",
    unableToObtain: false,
  });
  const [vitalsForm, setVitalsForm] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeSection, setActiveSection] = useState("patientInfo");

  const [loading, setLoading] = useState(true);
  const [creatingEncounter, setCreatingEncounter] = useState(false);
  const [savingEncounter, setSavingEncounter] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [autoSaveStatus, setAutoSaveStatus] = useState("Saved");
  const autoSaveTimerRef = useRef(null);
  const autoSaveStartedRef = useRef(false);
  const manualSaveInProgressRef = useRef(false);
  const suppressAutoSaveRef = useRef(false);
  const [expandedSections, setExpandedSections] = useState(defaultExpandedSections);
  const [generatingIncidentReport, setGeneratingIncidentReport] = useState(false);
  const [generatingRx, setGeneratingRx] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [lockLoading, setLockLoading] = useState(true);
  const [lockInfo, setLockInfo] = useState(null);
  const sessionIdRef = useRef(
  window.localStorage.getItem("patientChartSessionId") ||
    crypto.randomUUID()
);
const readOnlyRef = useRef(false);

if (!window.localStorage.getItem("patientChartSessionId")) {
  window.localStorage.setItem("patientChartSessionId", sessionIdRef.current);
}

  const createEncounterNow = Boolean(location.state?.createEncounter);
  const isMobileChart = window.innerWidth <= 768;

  const patientInfoRef = useRef(null);
  const summaryRef = useRef(null);
  const vitalsRef = useRef(null);
  const documentsRef = useRef(null);
  const medicalRef = useRef(null);
  const treatmentsRef = useRef(null);
  const rxRef = useRef(null);
  const incidentRef = useRef(null);
  const proceduresRef = useRef(null);
  const interventionsRef = useRef(null);
  const assessmentsRef = useRef(null);
  const medicationsRef = useRef(null);
  const signaturesRef = useRef(null);
  const historyRef = useRef(null);
  const manualScrollRef = useRef(false);

  const sectionRefs = {
  patientInfo: patientInfoRef,
  summary: summaryRef,
  vitals: vitalsRef,
  documents: documentsRef,
  medical: medicalRef,
  procedures: proceduresRef,
  interventions: interventionsRef,
  assessments: assessmentsRef,
  medications: medicationsRef,
  rx: rxRef,
  incident: incidentRef,
  signatures: signaturesRef,
  history: historyRef,
};

/* =========================
   VALIDATION HELPERS
========================= */

  const providerOptions = useMemo(
    () => employees.filter(isProviderEmployee).map(getEmployeeDisplayName).filter(Boolean).sort(),
    [employees]
  );

  const rxProviderOptions = useMemo(
  () =>
    employees
      .filter(isRxPrescriberEmployee)
      .sort((a, b) =>
        String(getEmployeeDisplayName(a)).localeCompare(
          String(getEmployeeDisplayName(b))
        )
      ),
  [employees]
);

  const reportCompletedOptions = useMemo(
    () => employees.map(getEmployeeDisplayName).filter(Boolean).sort(),
    [employees]
  );

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const hasRequiredVitalsSet = vitalsForm.some(
  (vital) =>
    !isBlank(vital.systolic) &&
    !isBlank(vital.diastolic) &&
    !isBlank(vital.pulse) &&
    !isBlank(vital.resp)
);

const patientErrors = [
  isBlank(patient?.fullName),
  isBlank(patient?.dob),
  isBlank(patient?.age),
  isBlank(patient?.gender),
  isBlank(patient?.phone),
  isBlank(patient?.email),
  isBlank(patient?.streetAddress),
  isBlank(patient?.city),
  isBlank(patient?.state),
  isBlank(patient?.zip),
  !patient?.allergiesNone && isBlank(patient?.allergies),
  !patient?.medicationsNone && isBlank(patient?.medications),
  !patient?.pastMedicalHistoryNone &&
  isBlank(patient?.pastMedicalHistory),
].filter(Boolean).length;

const summaryErrors = [
  isBlank(encounterForm.status),
  isBlank(encounterForm.arrivalDate),
  isBlank(encounterForm.arrivalTime),
  // Bed is optional
  isBlank(encounterForm.seenAt),
  isBlank(encounterForm.source),
  isBlank(encounterForm.chiefComplaint),
  isBlank(encounterForm.providerName),
  isBlank(encounterForm.reportCompletedBy),
].filter(Boolean).length;

const vitalsErrors = hasRequiredVitalsSet ? 0 : 1;

const medicalErrors = [
  isBlank(medicalForm.hpiComplaint),
  isBlank(medicalForm.assessment),
  isBlank(medicalForm.disposition),
  isBlank(medicalForm.planTreatmentDisposition),
].filter(Boolean).length;

const signatureErrors = [
  !signatureForm.unableToObtain && isBlank(signatureForm.patientSignature),
].filter(Boolean).length;

const incidentRequired =
  encounterForm?.incidentRequired === true ||
  incidentForm?.required === true; // fallback if you store it here later

const incidentComplete =
  Array.isArray(documents) &&
  documents.some(
    (doc) =>
      doc?.category === "incident-report" ||
      doc?.type === "incident-report"
  );

const incidentErrors = incidentRequired && !incidentComplete ? 1 : 0;

function getMissingCount(sectionKey) {
  switch (sectionKey) {
    case "patientInfo":
      return patientErrors;
    case "summary":
      return summaryErrors;
    case "vitals":
      return vitalsErrors;
    case "medical":
      return medicalErrors;
    case "signature":
    case "signatures":
      return signatureErrors;
      case "incident":
  return incidentErrors;
    default:
      return 0;
  }
}

function isComplete(sectionKey) {
  return getMissingCount(sectionKey) === 0;
}

async function handleForceOpenChart() {
  const confirmed = window.confirm(
    "Force open this chart? Only do this if nobody else is actively charting."
  );

  if (!confirmed) return;

  setLockLoading(true);
  setError("");
  setSaveMessage("");

  try {
    const result = await claimPatientChartLock(
      patientId,
      sessionIdRef.current,
      true
    );

    if (!result?.ok) {
      throw new Error("Lock was not claimed.");
    }

    readOnlyRef.current = false;
    setReadOnlyMode(false);
    setLockInfo(result.lock || null);
    setAutoSaveStatus("Saved");
    setSaveMessage("Chart force opened. You can now edit.");
  } catch (err) {
    console.error("Error force opening chart:", err);
    setError("Could not force open chart.");
  } finally {
    setLockLoading(false);
  }
}

async function ensureChartLock() {
  if (readOnlyRef.current) {
    setError("This chart is locked in view-only mode.");
    return false;
  }

  const hasLock = await verifyPatientChartLock(patientId, sessionIdRef.current);

  if (!hasLock) {
    readOnlyRef.current = true;
    setReadOnlyMode(true);
    setAutoSaveStatus("Locked");
    setError("Chart lock was lost. This chart is now view-only.");
    return false;
  }

  await touchPatientChartLock(patientId, sessionIdRef.current);
  return true;
}

  useEffect(() => {
    let cancelled = false;

    async function loadEmployees() {
      try {
        const snap = await getDocs(collection(db, "users"));
        if (cancelled) return;

        const items = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setEmployees(items);
      } catch (err) {
        console.error("Error loading employee autocomplete data:", err);
      }
    }

    loadEmployees();

    return () => {
      cancelled = true;
    };
  }, []);

useEffect(() => {
  let cancelled = false;

  async function claimLock() {
    setLockLoading(true);

    try {
      const result = await claimPatientChartLock(
        patientId,
        sessionIdRef.current,
        false
      );

      if (cancelled) return;

      setLockInfo(result.lock || null);

      if (result.readOnly) {
        readOnlyRef.current = true;
        setReadOnlyMode(true);
        setAutoSaveStatus("Locked");
      } else {
        readOnlyRef.current = false;
        setReadOnlyMode(false);
      }
    } catch (err) {
      console.error("Error claiming chart lock:", err);
      if (!cancelled) {
        readOnlyRef.current = true;
        setReadOnlyMode(true);
        setError("Could not claim chart lock. Chart opened in view-only mode.");
      }
    } finally {
      if (!cancelled) setLockLoading(false);
    }
  }

  claimLock();

  return () => {
    cancelled = true;
    releasePatientChartLock(patientId, sessionIdRef.current).catch(() => {});
  };
}, [patientId]);

useEffect(() => {
  if (readOnlyMode || lockLoading) return;

  const interval = window.setInterval(async () => {
    const ok = await touchPatientChartLock(patientId, sessionIdRef.current);

    if (!ok) {
      readOnlyRef.current = true;
      setReadOnlyMode(true);
      setAutoSaveStatus("Locked");
      setError("Chart lock expired or was taken over. This chart is now view-only.");
    }
  }, 60 * 1000);

  return () => window.clearInterval(interval);
}, [patientId, readOnlyMode, lockLoading]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      setSaveMessage("");
      suppressAutoSaveRef.current = true;
if (autoSaveTimerRef.current) {
  clearTimeout(autoSaveTimerRef.current);
  autoSaveTimerRef.current = null;
}
      try {
        const [patientData, encounterData] = await Promise.all([
          getPatientById(patientId),
          listEncounters(patientId),
        ]);

        if (cancelled) return;

        setPatient((prev) => ({
  ...(prev || {}),
  ...(patientData || {}),
}));
        setEncounters(encounterData);

        if (encounterData.length > 0) {
          setSelectedEncounterId(encounterData[0].id);
          setEncounterForm(buildEncounterForm(encounterData[0]));
          setMedicalForm(buildMedicalForm(encounterData[0]));
          setIncidentForm(buildIncidentForm(encounterData[0]));
          setSignatureForm(buildSignatureForm(encounterData[0]));
          setVitalsForm(buildVitalsForm(encounterData[0]));
          setDocuments(buildDocuments(encounterData[0]));
        } else {
          setSelectedEncounterId("");
          setEncounterForm(blankEncounterForm);
          setMedicalForm(blankMedicalForm);
          setIncidentForm(blankIncident());
          setSignatureForm({
            patientSignature: "",
            providerSignature: "",
            unableToObtain: false,
          });
          setVitalsForm([]);
          setDocuments([]);
        }

        if (createEncounterNow && patientData) {
          if (encounterData.length === 0) {
            await handleCreateEncounter(patientData);
          } else {
            const mostRecent = encounterData[0];
            if (!mostRecent || String(mostRecent.status || "").toLowerCase() === "closed") {
              await handleCreateEncounter(patientData);
            }
          }
        }
      } catch (err) {
        console.error("Error loading patient chart:", err);
        if (!cancelled) setError("Could not load patient chart.");
      } finally {
       if (!cancelled) {
  autoSaveStartedRef.current = false;
  suppressAutoSaveRef.current = false;
  setLoading(false);
} 
      }
    }

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length) {
          const sectionKey = visibleEntries[0].target?.dataset?.sectionKey;
          if (sectionKey && !manualScrollRef.current) {
          setActiveSection(sectionKey);
        }
        }
      },
      {
        root: null,
        rootMargin: "-90px 0px -55% 0px",
        threshold: [0.2, 0.35, 0.5, 0.75],
      }
    );

    sectionOrder.forEach((key) => {
      const node = sectionRefs[key]?.current;
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, expandedSections, selectedEncounterId, encounters.length]);

  function buildEncounterForm(encounter) {
    return {
      status: encounter.status || "open",
      arrivalDate: encounter.arrivalDate || "",
      arrivalTime: encounter.arrivalTime || "",
      chiefComplaint: encounter.chiefComplaint || "",
      bed: encounter.bed || "",
      seenAt: encounter.seenAt || "HQ",
      source: encounter.source || "",
      providerName: encounter.providerName || "",
      reportCompletedBy: encounter.reportCompletedBy || "",
      notes: encounter.notes || "",
      incidentRequired: Boolean(encounter.incidentRequired),
      incidentStatus: encounter.incidentStatus || "not-required",
    };
  }

  function buildMedicalForm(encounter) {
    const medical = encounter.medical || {};

    return {
      hpiComplaint:
        medical.hpiComplaint ||
        medical.complaintNotes ||
        medical.historyPresentIllness ||
        "",
      assessment: medical.assessment || "",
      planTreatmentDisposition:
        medical.planTreatmentDisposition ||
        medical.notes ||
        medical.dispositionNotes ||
        medical.followUpNotes ||
        "",
      treatments: Array.isArray(medical.treatments) ? medical.treatments : [],
      bodyPartInjured: medical.bodyPartInjured || "",
      firstAidGiven: Boolean(medical.firstAidGiven),
      disposition: medical.disposition || "",
    };
  }

  function buildIncidentForm(encounter) {
    const incident = encounter.incident || {};
    const blank = blankIncident();

    return {
      ...blank,
      ...incident,
      conditions: {
        ...blank.conditions,
        ...(incident.conditions || {}),
      },
      causes: {
        ...blank.causes,
        ...(incident.causes || {}),
      },
      location: {
        ...blank.location,
        ...(incident.location || {}),
      },
    };
  }

  function buildSignatureForm(encounter) {
    const sig = encounter.signature || {};
    return {
      patientSignature: sig.patientSignature || "",
      providerSignature: sig.providerSignature || "",
      unableToObtain: Boolean(sig.unableToObtain),
    };
  }

  function buildVitalsForm(encounter) {
    const vitals = Array.isArray(encounter.vitals) ? encounter.vitals : [];

    return vitals.map((vital) => {
      const parsedBp = parseBp(vital.bp || "");
      const parsedTemp = parseTemp(vital.temp || "");

      return {
        time: vital.time || "",
        systolic: vital.systolic || parsedBp.systolic,
        diastolic: vital.diastolic || parsedBp.diastolic,
        bp: vital.bp || buildBpString(vital.systolic, vital.diastolic),
        pulse: String(vital.pulse || ""),
        resp: String(vital.resp || ""),
        spo2: String(vital.spo2 || ""),
        tempValue: vital.tempValue || parsedTemp.tempValue,
        tempUnit: vital.tempUnit || parsedTemp.tempUnit || "F",
        temp: vital.temp || buildTempString(vital.tempValue, vital.tempUnit),
        pain: String(vital.pain || ""),
        gcsEye: String(vital.gcsEye || ""),
        gcsVerbal: String(vital.gcsVerbal || ""),
        gcsMotor: String(vital.gcsMotor || ""),
        gcs:
          String(vital.gcs || "") ||
          getGcsTotal(vital.gcsEye, vital.gcsVerbal, vital.gcsMotor),
        etco2: String(vital.etco2 || ""),
        rhythm: vital.rhythm || "",
        rhythmOther: vital.rhythmOther || "",
        notes: vital.notes || "",
      };
    });
  }

  function buildDocuments(encounter) {
    return Array.isArray(encounter.documents) ? encounter.documents : [];
  }

  function serializeMedicalForSave() {
    return {
      hpiComplaint: medicalForm.hpiComplaint || "",
      complaintNotes: medicalForm.hpiComplaint || "",
      historyPresentIllness: medicalForm.hpiComplaint || "",
      assessment: medicalForm.assessment || "",
      planTreatmentDisposition: medicalForm.planTreatmentDisposition || "",
      treatments: Array.isArray(medicalForm.treatments) ? medicalForm.treatments : [],
      treatmentRendered: buildTreatmentNarrative(medicalForm.treatments || []),
      disposition: medicalForm.disposition || "",
      dispositionNotes: medicalForm.planTreatmentDisposition || "",
      notes: medicalForm.planTreatmentDisposition || "",
      followUpNotes: "",
    };
  }

  function serializeVitalsForSave() {
    return vitalsForm.map((vital) => {
      const bp = buildBpString(vital.systolic, vital.diastolic);
      const gcs = getGcsTotal(vital.gcsEye, vital.gcsVerbal, vital.gcsMotor);
      const temp = buildTempString(vital.tempValue, vital.tempUnit);

      return {
        time: vital.time || "",
        systolic: vital.systolic || "",
        diastolic: vital.diastolic || "",
        bp,
        pulse: vital.pulse || "",
        resp: vital.resp || "",
        spo2: vital.spo2 || "",
        tempValue: vital.tempValue || "",
        tempUnit: vital.tempUnit || "F",
        temp,
        pain: vital.pain || "",
        gcsEye: vital.gcsEye || "",
        gcsVerbal: vital.gcsVerbal || "",
        gcsMotor: vital.gcsMotor || "",
        gcs,
        etco2: vital.etco2 || "",
        rhythm: vital.rhythm || "",
        rhythmOther: vital.rhythm === "Other" ? vital.rhythmOther || "" : "",
        notes: vital.notes || "",
      };
    });
  }

  async function reloadPatientChart(preferredEncounterId = "") {
    const [patientData, encounterData] = await Promise.all([
      getPatientById(patientId),
      listEncounters(patientId),
    ]);

   setPatient((prev) => ({
  ...(prev || {}),
  ...(patientData || {}),
}));
    setEncounters(encounterData);

    const nextSelectedId =
      preferredEncounterId ||
      selectedEncounterId ||
      (encounterData.length ? encounterData[0].id : "");

    if (nextSelectedId) {
      const nextEncounter =
        encounterData.find((encounter) => encounter.id === nextSelectedId) || encounterData[0];

      if (nextEncounter) {
        setSelectedEncounterId(nextEncounter.id);
        setEncounterForm(buildEncounterForm(nextEncounter));
        setMedicalForm(buildMedicalForm(nextEncounter));
        setIncidentForm(buildIncidentForm(nextEncounter));
        setSignatureForm(buildSignatureForm(nextEncounter));
        setVitalsForm(buildVitalsForm(nextEncounter));
        setDocuments(buildDocuments(nextEncounter));
      }
    } else {
      setSelectedEncounterId("");
      setEncounterForm(blankEncounterForm);
      setMedicalForm(blankMedicalForm);
      setIncidentForm(blankIncident());
      setSignatureForm({
        patientSignature: "",
        providerSignature: "",
        unableToObtain: false,
      });
      setVitalsForm([]);
      setDocuments([]);
    }
  }

async function handleAddToTracker() {

  if (readOnlyMode) {
  setError("This chart is locked in view-only mode.");
  return;
}

const canSave = await ensureChartLock();
if (!canSave) return;
  const bed = window.prompt("Enter bed number / location:");

  if (!bed) return;

  setError("");
  setSaveMessage("");

  try {
    let encounterId = selectedEncounterId;

    if (!encounterId) {
      encounterId = await createEncounter(patientId, {
        currentEncounterCount: Number(patient?.encounterCount || 0),
      });
      setSelectedEncounterId(encounterId);
    }

   const payload = {
  ...buildEncounterSavePayload(),

  status: "open",
  trackerStatus: "active",
  patientStatus: "active",

  seenAt: "HQ",
  location: "HQ",

  bed,
  currentBed: bed,
  hqBed: bed,
  assignedBed: bed,
  bedNumber: bed,

  arrivalDate:
    encounterForm.arrivalDate || new Date().toISOString().slice(0, 10),
  arrivalTime:
    encounterForm.arrivalTime ||
    new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),

  patientId,
  patientName: patient?.fullName || "",
  fullName: patient?.fullName || "",
  name: patient?.fullName || "",

  updatedAt: serverTimestamp(),
};

    
    await updateEncounter(patientId, encounterId, payload);

    await updateDoc(doc(db, "patients", patientId), {
  currentEncounterId: encounterId,
  currentBed: bed,
  bed,
  hqBed: bed,
  assignedBed: bed,
  bedNumber: bed,
  seenAt: "HQ",
  location: "HQ",
  status: "open",
  trackerStatus: "active",
  patientStatus: "active",
  chiefComplaint: encounterForm.chiefComplaint || "",
  currentChiefComplaint: encounterForm.chiefComplaint || "",
  updatedAt: serverTimestamp(),
});
    setEncounterForm((prev) => ({
      ...prev,
      status: payload.status,
      seenAt: "HQ",
      bed,
      arrivalDate: payload.arrivalDate,
      arrivalTime: payload.arrivalTime,
    }));

    await reloadPatientChart(encounterId);

    setSaveMessage(`Patient added to tracker in ${bed}.`);
    navigate("/field-access/hq", { state: { activeTab: "tracker" } });
  } catch (err) {
    console.error("Error adding patient to tracker:", err);
    setError("Could not add patient to tracker.");
  }
}

  async function handleCreateEncounter(patientOverride = null) {
    const sourcePatient = patientOverride || patient;
    if (!sourcePatient) return;

    setCreatingEncounter(true);
    setError("");
    setSaveMessage("");

    try {
      const encounterId = await createEncounter(patientId, {
        currentEncounterCount: Number(sourcePatient.encounterCount || 0),
      });

      await reloadPatientChart(encounterId);
      setSaveMessage("Encounter created.");
    } catch (err) {
      console.error("Error creating encounter:", err);
      setError("Could not create encounter.");
    } finally {
      setCreatingEncounter(false);
    }
  }

  function buildEncounterSavePayload() {
  return {
    ...encounterForm,
    medical: serializeMedicalForSave(),
    incident: incidentForm,
    signature: signatureForm,
    vitals: serializeVitalsForSave(),
    documents,
  };
}

useEffect(() => {
  if (
    !selectedEncounterId ||
    loading ||
    lockLoading ||
    readOnlyMode ||
    suppressAutoSaveRef.current
  ) {
    return;
  }

  // Prevent autosave from immediately firing during initial page load
  if (!autoSaveStartedRef.current) {
    autoSaveStartedRef.current = true;
    return;
  }

  const encounterIdForSave = selectedEncounterId;
  setAutoSaveStatus("Saving...");

  if (autoSaveTimerRef.current) {
    clearTimeout(autoSaveTimerRef.current);
  }

  autoSaveTimerRef.current = setTimeout(async () => {
if (manualSaveInProgressRef.current) return;

    try {
  const canSave = await ensureChartLock();
  if (!canSave) return;

  if (encounterIdForSave !== selectedEncounterId) return;

await updateEncounter(patientId, encounterIdForSave, buildEncounterSavePayload());

     const firstName = patient?.firstName || "";
const lastName = patient?.lastName || "";
const fullName =
  patient?.fullName ||
  [firstName, lastName].filter(Boolean).join(" ");

await updateDoc(doc(db, "patients", patientId), {
  firstName,
  lastName,
  fullName,
  fullNameLower: String(fullName).toLowerCase(),

  dob: patient?.dob || "",
  age: patient?.age || "",
  gender: patient?.gender || "",
  phone: patient?.phone || "",
  email: patient?.email || "",

  streetAddress: patient?.streetAddress || "",
  city: patient?.city || "",
  state: patient?.state || "",
  zip: patient?.zip || "",

  allergies: patient?.allergies || "",
  allergiesNone: Boolean(patient?.allergiesNone),

  medications: patient?.medications || "",
  medicationsNone: Boolean(patient?.medicationsNone),

  pastMedicalHistory: patient?.pastMedicalHistory || "",
  pastMedicalHistoryNone: Boolean(patient?.pastMedicalHistoryNone),

  chiefComplaint: encounterForm.chiefComplaint || "",
  currentChiefComplaint: encounterForm.chiefComplaint || "",

  updatedAt: serverTimestamp(),
});

      setAutoSaveStatus("Saved");
    } catch (err) {
      console.error("Autosave failed:", err);
      setAutoSaveStatus("Autosave failed");
    }
  }, 1000);

  return () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
  };
}, [
  patientId,
  selectedEncounterId,
  loading,
  encounterForm,
  medicalForm,
  incidentForm,
  signatureForm,
  vitalsForm,
  documents,
  patient,
  readOnlyMode,
lockLoading,
]);

  async function handleSaveEncounter() {
    if (readOnlyMode) {
  setError("This chart is locked in view-only mode and cannot be saved.");
  return;
}

const canSave = await ensureChartLock();
if (!canSave) return;

    manualSaveInProgressRef.current = true;
    suppressAutoSaveRef.current = true;

if (autoSaveTimerRef.current) {
  clearTimeout(autoSaveTimerRef.current);
  autoSaveTimerRef.current = null;
}
    if (!selectedEncounterId) {
      setError("Create an encounter first.");
      return;
    }

    setSavingEncounter(true);
    setError("");
    setSaveMessage("");

    try {
     await updateEncounter(patientId, selectedEncounterId, buildEncounterSavePayload());

const firstName = patient?.firstName || "";
const lastName = patient?.lastName || "";
const fullName =
  patient?.fullName ||
  [firstName, lastName].filter(Boolean).join(" ");

await updateDoc(doc(db, "patients", patientId), {
  ...patient,
  firstName,
  lastName,
  fullName,
  fullNameLower: String(fullName).toLowerCase(),

  chiefComplaint: encounterForm.chiefComplaint || "",
  currentChiefComplaint: encounterForm.chiefComplaint || "",

  updatedAt: serverTimestamp(),
});
      
setSaveMessage("Encounter saved.");

if (isHQMode) {
  navigate("/field-access/hq", { state: { activeTab: "patients" } });
} else {
  navigate("/patients");
}
    } catch (err) {
      console.error("Error saving encounter:", err);
      setError("Could not save encounter.");
    } finally {
    manualSaveInProgressRef.current = false;
suppressAutoSaveRef.current = false;
setSavingEncounter(false);
    }
  }

 async function handleGenerateIncidentReport() {
  if (readOnlyRef.current) return;
  if (!selectedEncounterId) {
    setError("Create or select an encounter first.");
    return;
  }

  setGeneratingIncidentReport(true);
  setError("");
  setSaveMessage("");

  try {
    const encounterSnapshot = {
      id: selectedEncounterId,
      ...encounterForm,
      medical: serializeMedicalForSave(),
      incident: incidentForm,
      signature: signatureForm,
      vitals: serializeVitalsForSave(),
      documents,
    };

    const templateData = buildIncidentReportTemplateData({
      patient,
      encounter: encounterSnapshot,
      incidentForm,
      medicalForm,
      signatureForm,
    });

    const result = await generateIncidentReportPdf({
      patientId,
      encounterId: selectedEncounterId,
      templateData,
      patient,
      encounter: encounterSnapshot,
      photos: Array.isArray(encounterSnapshot?.incident?.photos)
        ? encounterSnapshot.incident.photos.map((photo) => {
            const raw =
              photo?.dataUrl ||
              photo?.base64 ||
              photo?.url ||
              photo?.preview ||
              "";

            const base64 = String(raw).includes(",")
              ? String(raw).split(",")[1]
              : String(raw);

            return {
              base64: base64 || "",
              name: photo?.name || "",
            };
          })
        : [],
    });

    if (!result?.pdfUrl || !result?.driveFileId) {
      throw new Error("Final Drive PDF was not returned.");
    }

    const generatedDoc = buildIncidentDocumentRecord({
      patient,
      encounterForm,
      result,
    });

    const existingDocs = Array.isArray(documents) ? documents : [];
    const updatedDocs = [...existingDocs, generatedDoc];

    setDocuments(updatedDocs);

    await updateEncounter(patientId, selectedEncounterId, {
      documents: updatedDocs,
    });

    await addDoc(collection(db, "incident_reports"), {
      createdAt: serverTimestamp(),
      generatedAt: result.generatedAt || new Date().toISOString(),
      patientId,
      encounterId: selectedEncounterId,
      patient,
      encounter: encounterSnapshot,
      incidentForm: {
        ...incidentForm,
        photos: (incidentForm.photos || []).map((photo) => ({
          name: photo.name || "",
        })),
      },
      medicalForm,
      signatureForm: {
        patientSignature: Boolean(signatureForm.patientSignature),
        providerSignature: Boolean(signatureForm.providerSignature),
        unableToObtain: Boolean(signatureForm.unableToObtain),
      },
      fileName:
        result.fileName ||
        `Incident Report - ${patient?.fullName || "Unknown Patient"}.pdf`,
      pdfUrl: result.pdfUrl || "",
      driveFileId: result.driveFileId || "",
      templateId: result.templateId || "",
      category: "incident-report",
    });

    setSaveMessage("Incident Report PDF generated and saved to encounter.");
  } catch (err) {
    console.error("Error generating incident report:", err);
    setError(err?.message || "Could not generate Incident Report PDF.");
  } finally {
    setGeneratingIncidentReport(false);
  }
}

async function handleGenerateRx({
  providerUid,
  providerPin,
  medications,
}) {
  if (readOnlyRef.current) return;
  if (!selectedEncounterId) {
    alert("Select or create an encounter first.");
    return;
  }

  try {
    setGeneratingRx(true);

 const selectedProvider = employees.find(
  (employee) =>
    String(employee.uid || employee.id) === String(providerUid)
);
const result = await generateRxPrescriptionPdf({
  patientId,
  encounterId: selectedEncounterId,
  patient,
  encounter: encounterForm,
  providerUid,
  providerPin,
  provider: selectedProvider || null,
  providerName: getEmployeeDisplayName(selectedProvider || {}),
  providerDEA:
    selectedProvider?.dea ||
    selectedProvider?.deaNumber ||
    selectedProvider?.DEA ||
    "",
  providerNPI:
    selectedProvider?.npi ||
    selectedProvider?.npiNumber ||
    selectedProvider?.NPI ||
    "",
  medications,
}); 

    const rxDocument = buildRxDocumentRecord({
      patient,
      encounterForm,
      result,
    });

    const updatedDocuments = [...(documents || []), rxDocument];

    setDocuments(updatedDocuments);

    const encounterRef = doc(
      db,
      "patients",
      patientId,
      "encounters",
      selectedEncounterId
    );

    await updateDoc(encounterRef, {
      documents: updatedDocuments,
      rxPdfUrl: result?.pdfUrl || "",
      rxGeneratedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("Prescription generated successfully.");
  } catch (error) {
    console.error("RX generation error:", error);
    alert(error?.message || "Failed to generate prescription.");
  } finally {
    setGeneratingRx(false);
  }
}

  async function handleUploadDocument(e) {
    if (readOnlyRef.current) return;
    const file = e.target.files?.[0];
    if (!file || !selectedEncounterId) return;

    setUploading(true);
    setError("");
    setSaveMessage("");

    try {
      const docMeta = await uploadEncounterDocument(patientId, selectedEncounterId, file);
      const updatedDocs = [...documents, docMeta];

      setDocuments(updatedDocs);

      await updateEncounter(patientId, selectedEncounterId, {
        documents: updatedDocs,
      });

      setSaveMessage("Document uploaded.");
    } catch (err) {
      console.error("Error uploading document:", err);
      setError("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

 async function handleDeleteDocument(docItem, index) {
  if (readOnlyRef.current) return;
  if (!selectedEncounterId) return;

  const confirmed = window.confirm(
    `Delete "${docItem.name || "this document"}" from this chart?`
  );
  if (!confirmed) return;

  setError("");
  setSaveMessage("");

  try {
    const updatedDocs = documents.filter((_, i) => i !== index);

    setDocuments(updatedDocs);

    await updateEncounter(patientId, selectedEncounterId, {
      documents: updatedDocs,
    });

    setSaveMessage("Document removed from chart.");
  } catch (err) {
    console.error("Error deleting document:", err);
    setError("Could not delete document.");
  }
}

async function handleScannedPdfUpload(file) {
  if (readOnlyRef.current) return;
  if (!file || !selectedEncounterId) return;

  setUploading(true);
  setError("");
  setSaveMessage("");

  try {
    const docMeta = await uploadEncounterDocument(
      patientId,
      selectedEncounterId,
      file
    );

    const updatedDocs = [...documents, docMeta];

    setDocuments(updatedDocs);

    await updateEncounter(patientId, selectedEncounterId, {
      documents: updatedDocs,
    });

    setSaveMessage("Scanned document uploaded.");
  } catch (err) {
    console.error("Error uploading scanned document:", err);
    setError("Scan upload failed.");
  } finally {
    setUploading(false);
  }
}
    

  function setEncounterField(field, value) {
     if (readOnlyRef.current) return;
    setEncounterForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function setMedicalField(field, value) {
     if (readOnlyRef.current) return;
    setMedicalForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function setVitalField(index, field, value) {
     if (readOnlyRef.current) return;
    setVitalsForm((prev) =>
      prev.map((vital, i) => {
        if (i !== index) return vital;

        const next = {
          ...vital,
          [field]: value,
        };

        if (field === "systolic" || field === "diastolic") {
          next[field] = digitsOnly(value).slice(0, 3);
          next.bp = buildBpString(
            field === "systolic" ? next.systolic : vital.systolic,
            field === "diastolic" ? next.diastolic : vital.diastolic
          );
        }

        if (field === "pulse" || field === "resp" || field === "spo2" || field === "etco2") {
          next[field] = digitsOnly(value).slice(0, 3);
        }

        if (field === "tempValue") {
          next.tempValue = String(value || "").replace(/[^\d.]/g, "").slice(0, 5);
          next.temp = buildTempString(next.tempValue, next.tempUnit);
        }

        if (field === "tempUnit") {
          next.tempUnit = value || "F";
          next.temp = buildTempString(next.tempValue, next.tempUnit);
        }

        if (field === "gcsEye" || field === "gcsVerbal" || field === "gcsMotor") {
          next.gcs = getGcsTotal(
            field === "gcsEye" ? value : next.gcsEye,
            field === "gcsVerbal" ? value : next.gcsVerbal,
            field === "gcsMotor" ? value : next.gcsMotor
          );
        }

        if (field === "rhythm" && value !== "Other") {
          next.rhythmOther = "";
        }

        return next;
      })
    );
  }

  function addVital() {
     if (readOnlyRef.current) return;
    setVitalsForm((prev) => [...prev, blankVital()]);
  }

  function removeVital(index) {
     if (readOnlyRef.current) return;
    setVitalsForm((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSelectEncounter(encounter) {
   suppressAutoSaveRef.current = true;

if (autoSaveTimerRef.current) {
  clearTimeout(autoSaveTimerRef.current);
  autoSaveTimerRef.current = null;
} 
    setSelectedEncounterId(encounter.id);
    setEncounterForm(buildEncounterForm(encounter));
    setMedicalForm(buildMedicalForm(encounter));
    setIncidentForm(buildIncidentForm(encounter));
    setSignatureForm(buildSignatureForm(encounter));
    setVitalsForm(buildVitalsForm(encounter));
    setSaveMessage("");
    setError("");
    setDocuments(Array.isArray(encounter.documents) ? encounter.documents : []);
    window.setTimeout(() => {
  autoSaveStartedRef.current = false;
  suppressAutoSaveRef.current = false;
}, 0);
  }

  function scrollToSection(ref, sectionKey) {
  if (sectionKey) setActiveSection(sectionKey);

  manualScrollRef.current = true;

  ref?.current?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });

  window.setTimeout(() => {
    manualScrollRef.current = false;
    if (sectionKey) setActiveSection(sectionKey);
  }, 700);
}

  function toggleSection(sectionKey) {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }

 function expandAllSections() {
  setExpandedSections(defaultExpandedSections);
}

  function collapseAllSections() {
  setExpandedSections({
    patientInfo: false,
    summary: false,
    vitals: false,
    documents: false,
    medical: false,
    procedures: false,
    interventions: false,
    assessments: false,
    medications: false,
    rx: false,
    incident: false,
    signatures: false,
    history: false,
  });
}

  const sectionConfig = [
  {
    key: "patientInfo",
    label: "Patient Info",
    errorCount: patientErrors,
    component: (
      <PatientInfoSection
        patientForm={patient}
        setPatientField={setPatientField}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        sectionRef={patientInfoRef}
        activeSection={activeSection}
        readOnlyMode={readOnlyMode}
        errorCount={patientErrors}
      />
    ),
  },

  {
    key: "summary",
    label: "Summary",
    errorCount: summaryErrors,
    component: (
      <EncounterSummarySection
        selectedEncounterId={selectedEncounterId}
        encounterForm={encounterForm}
        setEncounterField={setEncounterField}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        sectionRef={summaryRef}
        activeSection={activeSection}
        readOnlyMode={readOnlyMode}
        errorCount={summaryErrors}
      />
    ),
  },

  {
    key: "vitals",
    label: "Vitals",
    errorCount: vitalsErrors,
    component: (
      <VitalsSection
        selectedEncounterId={selectedEncounterId}
        vitalsForm={vitalsForm}
        addVital={addVital}
        removeVital={removeVital}
        setVitalField={setVitalField}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        sectionRef={vitalsRef}
        activeSection={activeSection}
        readOnlyMode={readOnlyMode}
        errorCount={vitalsErrors}
      />
    ),
  },

 {
  key: "documents",
  label: "Documents",
  errorCount: 0,
  component: (
    <DocumentsSection
      selectedEncounterId={selectedEncounterId}
      documents={documents}
      uploading={uploading}
      handleUploadDocument={handleUploadDocument}
      handleDeleteDocument={handleDeleteDocument}
      setScanModalOpen={setScanModalOpen}
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={documentsRef}
      readOnlyMode={readOnlyMode}
      activeSection={activeSection}
    />
  ),
},

  {
    key: "medical",
    label: "Medical",
    errorCount: medicalErrors,
    component: (
      <MedicalSection
        selectedEncounterId={selectedEncounterId}
        medicalForm={medicalForm}
        setMedicalField={setMedicalField}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        sectionRef={medicalRef}
        activeSection={activeSection}
        readOnlyMode={readOnlyMode}
        errorCount={medicalErrors}
      />
    ),
  },

  {
  key: "procedures",
  label: "Procedures",
  errorCount: 0,
  component: (
    <SectionCard
      title="Procedures"
      sectionKey="procedures"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={proceduresRef}
      activeSection={activeSection}
    >
      <TreatmentSection
        treatments={medicalForm.treatments || []}
        categoryFilter="Procedure"
        defaultCategory="Procedure"
        readOnlyMode={readOnlyMode}
        defaultPerformedBy={encounterForm.providerName}
       setTreatments={(updater) => {
  if (readOnlyRef.current) return;

  setMedicalForm((prev) => ({
    ...prev,
    treatments:
      typeof updater === "function"
        ? updater(prev.treatments || [])
        : updater,
  }));
}}
        employeeOptions={reportCompletedOptions}
      />
    </SectionCard>
  ),
},
{
  key: "interventions",
  label: "Interventions",
  errorCount: 0,
  component: (
    <SectionCard
      title="Interventions"
      sectionKey="interventions"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={interventionsRef}
      activeSection={activeSection}
    >
      <TreatmentSection
        treatments={medicalForm.treatments || []}
        categoryFilter="Intervention"
        defaultCategory="Intervention"
        readOnlyMode={readOnlyMode}
        defaultPerformedBy={encounterForm.providerName}
        setTreatments={(updater) => {
  if (readOnlyRef.current) return;

  setMedicalForm((prev) => ({
    ...prev,
    treatments:
      typeof updater === "function"
        ? updater(prev.treatments || [])
        : updater,
  }));
}}
        employeeOptions={reportCompletedOptions}
      />
    </SectionCard>
  ),
},
{
  key: "assessments",
  label: "Assessments",
  errorCount: 0,
  component: (
    <SectionCard
      title="Assessments"
      sectionKey="assessments"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={assessmentsRef}
      activeSection={activeSection}
    >
      <TreatmentSection
        treatments={medicalForm.treatments || []}
        categoryFilter="Assessment"
        defaultCategory="Assessment"
        readOnlyMode={readOnlyMode}
        defaultPerformedBy={encounterForm.providerName}
        setTreatments={(updater) => {
  if (readOnlyRef.current) return;

  setMedicalForm((prev) => ({
    ...prev,
    treatments:
      typeof updater === "function"
        ? updater(prev.treatments || [])
        : updater,
  }));
}}
        employeeOptions={reportCompletedOptions}
      />
    </SectionCard>
  ),
},
{
  key: "medications",
  label: "Medications",
  errorCount: 0,
  component: (
    <SectionCard
      title="Medications"
      sectionKey="medications"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={medicationsRef}
      activeSection={activeSection}
    >
      <TreatmentSection
        treatments={medicalForm.treatments || []}
        categoryFilter="Medication"
        defaultCategory="Medication"
        readOnlyMode={readOnlyMode}
        defaultPerformedBy={encounterForm.providerName}
       setTreatments={(updater) => {
  if (readOnlyRef.current) return;

  setMedicalForm((prev) => ({
    ...prev,
    treatments:
      typeof updater === "function"
        ? updater(prev.treatments || [])
        : updater,
  }));
}}
        employeeOptions={reportCompletedOptions}
      />
    </SectionCard>
  ),
},

  {
    key: "rx",
    label: "RX",
    errorCount: 0,
    component: (
      <RXSection
        selectedEncounterId={selectedEncounterId}
        patient={patient}
        encounter={encounterForm}
        providers={rxProviderOptions}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        sectionRef={rxRef}
        activeSection={activeSection}
        onGenerateRx={handleGenerateRx}
        readOnlyMode={readOnlyMode}
        isGeneratingRx={generatingRx}
      />
    ),
  },

  {
    key: "incident",
    label: "Incident Report",
    errorCount: incidentErrors,
    component: (
      <IncidentSection
        selectedEncounterId={selectedEncounterId}
        incidentForm={incidentForm}
        errorCount={incidentErrors}
        setIncidentForm={setIncidentForm}
        medicalForm={medicalForm}
        setMedicalForm={setMedicalForm}
        encounterForm={encounterForm}
        setEncounterForm={setEncounterForm}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        sectionRef={incidentRef}
        activeSection={activeSection}
        readOnlyMode={readOnlyMode}
        onGenerateIncidentReport={handleGenerateIncidentReport}
      />
    ),
  },

  {
    key: "signatures",
    label: "Signatures",
    errorCount: signatureErrors,
    component: (
     <SignatureSection
  signatureForm={signatureForm}
  setSignatureField={setSignatureField}
  expandedSections={expandedSections}
  toggleSection={toggleSection}
  sectionRef={signaturesRef}
  activeSection={activeSection}
  errorCount={signatureErrors}
  readOnlyMode={readOnlyMode}
/> 
    ),
  },

 {
  key: "history",
  label: "History",
  errorCount: 0,
  component: (
    <SectionCard
      title="Encounter History"
      sectionKey="history"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={historyRef}
      activeSection={activeSection}
    >
      <div style={{ display: "grid", gap: 10 }}>
        {encounters.length === 0 ? (
          <div style={{ color: "#64748b" }}>No prior encounters found.</div>
        ) : (
          encounters.map((encounter) => {
            const isSelected = encounter.id === selectedEncounterId;

            return (
              <button
                key={encounter.id}
                type="button"
                onClick={() => handleSelectEncounter(encounter)}
                style={{
                  textAlign: "left",
                  border: isSelected
                    ? "2px solid #2563eb"
                    : "1px solid #cbd5e1",
                  borderRadius: 14,
                  padding: 12,
                  background: isSelected ? "#eff6ff" : "#fff",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 900 }}>
                  {encounter.arrivalDate || "No date"}{" "}
                  {encounter.arrivalTime || ""}
                </div>

                <div style={{ color: "#475569", marginTop: 4 }}>
                  {encounter.chiefComplaint || "No chief complaint"}
                </div>

                <div style={{ marginTop: 6 }}>
                  <span style={getStatusPillStyle(encounter.status)}>
                    {compactHistoryStatus(encounter.status)}
                  </span>
                </div>
              </button>

            );
          })
        )}
      </div>
    </SectionCard>
  ),
},
];

const activeSectionConfig =
  sectionConfig.find((s) => s.key === activeSection) ||
  sectionConfig[0];

  if (loading) {
    return <div className="patient-page-shell" style={pageStyle}>Loading patient chart...</div>;
  }

  if (error && !patient) {
    return (
      <div className="patient-page-shell" style={pageStyle}>
        <div style={{ color: "#b91c1c" }}>{error}</div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="patient-page-shell" style={pageStyle}>
        Patient not found.
      </div>
    );
  }

  return (
    <>
    <style>
      {`
        @keyframes incident-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}
    </style>

    {generatingIncidentReport && (
      <div style={overlayStyle}>
        <div style={overlayCardStyle}>
          <div style={spinnerStyle} />

          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            Generating Incident Report
          </div>

          <div
            style={{
              fontSize: 14,
              color: "#475569",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            Please wait while the report is created and saved.
          </div>
        </div>
      </div>
    )}
      <div
  className="patient-page-shell"
  
  style={{
    ...pageStyle,
    opacity: generatingIncidentReport ? 0.65 : 1,
    pointerEvents: generatingIncidentReport ? "none" : "auto",
    transition: "opacity 0.2s ease",
  }}
>

        <datalist id="provider-options">
          {providerOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>

        <datalist id="report-completed-options">
          {reportCompletedOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>

        <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 14,
  }}
>
  <div>
    <div
      style={{
        fontSize: 28,
        fontWeight: 900,
        lineHeight: 1,
        color: "#0f172a",
      }}
    >
      {patient.fullName || "Patient Chart"}
    </div>

    <div
      style={{
        marginTop: 8,
        color: "#64748b",
        fontSize: 15,
        fontWeight: 500,
      }}
    >
      {patient.dob ? `DOB: ${patient.dob}` : "DOB: —"}
      {patient.age ? ` • Age: ${patient.age}` : ""}
      {patient.gender ? ` • ${patient.gender}` : ""}
    </div>
  </div>

  <div
    style={{
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      style={createButtonStyle}
      onClick={() => handleCreateEncounter()}
      disabled={creatingEncounter || readOnlyMode || lockLoading}
    >
      {creatingEncounter ? "Creating..." : "Create Encounter"}
    </button>

<button
  type="button"
  style={primaryButtonStyle}
  onClick={handleAddToTracker}
  disabled={readOnlyMode || lockLoading}
>
  Add to Tracker
</button>

    <button
      type="button"
      style={primaryButtonStyle}
      onClick={handleSaveEncounter}
     disabled={savingEncounter || !selectedEncounterId || readOnlyMode || lockLoading}
    >
      {savingEncounter ? "Saving..." : "Save / Close"}
    </button>
  </div>
</div>

<div
  style={{
    marginTop: 6,
    fontSize: 13,
    fontWeight: 800,
    color:
      autoSaveStatus === "Autosave failed"
        ? "#b91c1c"
        : autoSaveStatus === "Saving..."
        ? "#92400e"
        : "#166534",
  }}
>
  {selectedEncounterId ? `Auto-save: ${autoSaveStatus}` : "Create an encounter to enable auto-save"}
</div>
{readOnlyMode && (
  <div
    style={{
      marginTop: 10,
      marginBottom: 14,
      padding: 14,
      borderRadius: 12,
      border: "1px solid #fca5a5",
      background: "#fef2f2",
      color: "#991b1b",
      fontWeight: 800,
      lineHeight: 1.4,
    }}
  >
    <div>
  🔒 This chart is currently open on another device. View-only mode is enabled.
  Editing, saving, signatures, uploads, and document generation are disabled.
</div>

<button
  type="button"
  onClick={handleForceOpenChart}
  disabled={lockLoading}
  style={{
    marginTop: 10,
    border: "1px solid #991b1b",
    borderRadius: 10,
    padding: "10px 14px",
    background: "#fff",
    color: "#991b1b",
    fontWeight: 900,
    cursor: lockLoading ? "not-allowed" : "pointer",
  }}
>
  {lockLoading ? "Unlocking..." : "Force Open Chart"}
</button>
  </div>
)}
        

        {(error || saveMessage) && (
          <div
            style={{
              ...basePanelStyle,
              marginBottom: 14,
              color: error ? "#b91c1c" : "#166534",
              borderColor: error ? "#fecaca" : "#bbf7d0",
              background: error ? "#fef2f2" : "#f0fdf4",
              padding: 12,
            }}
          >
            {error || saveMessage}
          </div>
        )}

       <div
  style={{
    display: "grid",
    gridTemplateColumns: window.innerWidth <= 600 ? "1fr" : "230px 1fr",
    gap: 16,
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    boxSizing: "border-box",
    alignItems: "start",
  }}
>
  <div
   style={{
  position: window.innerWidth <= 600 ? "relative" : "sticky",
  top: window.innerWidth <= 600 ? "auto" : 12,
  background: "#173b6d",
  borderRadius: 18,
  padding: 12,
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 8,
  maxHeight: window.innerWidth <= 600 ? "none" : "calc(100vh - 24px)",
  overflowY: window.innerWidth <= 600 ? "visible" : "auto",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
}}
  >
    {sectionConfig.map((section) => {
      const active = activeSection === section.key;
      const theme =
        sectionThemes[section.key] || sectionThemes.summary;

      return (
        <button
          key={section.key}
          type="button"
         onClick={() => {
  setExpandedSections((prev) => ({
    ...prev,
    [section.key]: true,
  }));

  window.requestAnimationFrame(() => {
    scrollToSection(sectionRefs[section.key], section.key);
  });
}}
          style={{
            border: "none",
            borderRadius: 12,
            padding: "14px 16px",
            background: active
              ? theme.bg
              : "rgba(255,255,255,.08)",
            color: active ? theme.text : "#fff",
            fontWeight: 700,
            fontSize: 14,
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{section.label}</span>

          {section.errorCount > 0 && (
            <span
              style={{
                minWidth: 24,
                height: 24,
                borderRadius: 999,
                background: "#ef4444",
                color: "#fff",
                fontSize: 11,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {section.errorCount}
            </span>
          )}
        </button>
      );
    })}
  </div>

  <div
  style={{
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    boxSizing: "border-box",
  }}
>
  {activeSectionConfig.component}

    <div
      style={{
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  marginTop: 18,
}}
    >
      <button
        type="button"
        style={buttonStyle}
        onClick={() => {
          const index = sectionConfig.findIndex(
            (s) => s.key === activeSection
          );

          if (index > 0) {
            setActiveSection(sectionConfig[index - 1].key);
          }
        }}
      >
        Previous
      </button>

      <button
        type="button"
        style={primaryButtonStyle}
        onClick={() => {
          const index = sectionConfig.findIndex(
            (s) => s.key === activeSection
          );

          if (index < sectionConfig.length - 1) {
            setActiveSection(sectionConfig[index + 1].key);
          }
        }}
      >
        Next
      </button>
    </div>
  </div>
</div>

      </div>

      <ScanDocumentModal
        open={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        onComplete={handleScannedPdfUpload}
        uploading={uploading}
      />
    </>
  );
}